// blockingStore.ts
// Global "blocking overlay" state for slow operations.
// Keep this file React-free so services can safely import start/stop helpers.

export type BlockingPayload = {
  title?: string
  description?: string
}

type Listener = () => void

type BlockingState = {
  count: number
  title?: string
  description?: string
}

let state: BlockingState = { count: 0 }
const listeners = new Set<Listener>()

const emit = () => {
  listeners.forEach(l => {
    try {
      l()
    } catch {
      // ignore subscriber errors
    }
  })
}

export const subscribeBlocking = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const getBlockingState = (): BlockingState => state

export const startBlocking = (payload?: BlockingPayload): (() => void) => {
  const isRoot = state.count === 0
  const next: BlockingState = {
    count: state.count + 1,
    // Only the outer-most operation controls the messaging to avoid flicker/confusing titles.
    title: isRoot ? (payload?.title ?? state.title) : state.title,
    description: isRoot ? (payload?.description ?? state.description) : state.description
  }
  state = next
  emit()

  let ended = false
  return () => {
    if (ended) return
    ended = true
    stopBlocking()
  }
}

export const stopBlocking = (): void => {
  const nextCount = Math.max(0, state.count - 1)
  state = nextCount === 0 ? { count: 0 } : { ...state, count: nextCount }
  emit()
}
